/**
 * renderSafe — never let one malformed notification row white-screen the app.
 * (notify-center, 7 Sep 2026)
 *
 * The legacy bell collection is not uniform.  `GET /api/notifications` returns
 * rows carrying object fields — `from: { name: "Vishakha Chauhan (DCS)",
 * email: "hr@daruwallas.com" }` — and at least one row shaped like a
 * mail-chime counts payload, `{ new, mailbox }`.  React throws
 * "Objects are not valid as a React child" on any of those, and because the
 * bell lives in the Header, that throw takes down every page.
 *
 * So nothing from a notification row is ever handed to JSX raw.  Everything
 * goes through `safeText()`:
 *
 *   string / finite number / bigint  -> as is
 *   null / undefined / boolean / NaN -> null (React renders nothing)
 *   Date                             -> ISO string
 *   React element                    -> passed through untouched
 *   Array                            -> its renderable parts, comma-joined
 *   object                           -> the first of name / title / label /
 *                                       email / text that is itself
 *                                       renderable, otherwise null
 *
 * Never `String(obj)`, so "[object Object]" can never reach the screen.
 *
 * `previewText()`, at the bottom, is the second half of the story: a mail body
 * turned into a line a person can read rather than a slice of raw HTML.
 */

/** Keys that carry a human-readable label on the shapes this app actually has. */
export const DISPLAY_KEYS = ['name', 'title', 'label', 'email', 'text'];

const MAX_DEPTH = 3;

const isReactElement = (value) =>
  !!value && typeof value === 'object' && (
    // React 19 uses a symbol; the string form covers older/serialised elements.
    value.$$typeof === Symbol.for('react.element') ||
    value.$$typeof === Symbol.for('react.transitional.element') ||
    value.$$typeof === Symbol.for('react.portal') ||
    (typeof value.$$typeof === 'string' && value.$$typeof.indexOf('react') === 0)
  );

/**
 * Turn any value into something React can render, or null.
 * Never throws — a getter that explodes still yields null.
 */
export function safeText(value, depth = 0) {
  try {
    if (value === null || value === undefined) return null;

    const t = typeof value;
    if (t === 'string') return value;
    if (t === 'number') return Number.isFinite(value) ? String(value) : null;
    if (t === 'bigint') return String(value);
    // A stray boolean on screen ("true") is noise, not information.
    if (t === 'boolean' || t === 'function' || t === 'symbol') return null;

    if (isReactElement(value)) return value;
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value.toISOString();
    }

    if (depth >= MAX_DEPTH) return null;

    if (Array.isArray(value)) {
      const parts = value
        .map((entry) => safeText(entry, depth + 1))
        .filter((entry) => typeof entry === 'string' && entry.length > 0);
      return parts.length ? parts.join(', ') : null;
    }

    if (t === 'object') {
      for (let i = 0; i < DISPLAY_KEYS.length; i += 1) {
        const candidate = safeText(value[DISPLAY_KEYS[i]], depth + 1);
        if (typeof candidate === 'string' && candidate.length > 0) return candidate;
      }
      // Nothing human-readable in here (a `{ new, mailbox }` counts payload,
      // say). Render nothing rather than guessing.
      return null;
    }

    return null;
  } catch (e) {
    return null;
  }
}

/** `safeText` with a fallback, for places that must show *something*. */
export const safeTextOr = (value, fallback) => {
  const out = safeText(value);
  return out === null || out === '' ? fallback : out;
};

/**
 * Flatten one notification row into scalars a component can render blindly.
 * Returns null only when there is no usable id AND nothing to show, so the
 * caller can drop the row entirely.
 */
export function safeNotification(row, index = 0) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return null;

  const id = safeText(row.id) || safeText(row._id) || `row-${index}`;
  const title = safeText(row.title) || safeText(row.subject) || safeText(row.heading);
  const body = safeText(row.message) || safeText(row.body) || safeText(row.snippet)
    || safeText(row.details) || safeText(row.description);
  // Last resort: the row has no title/body but does name a person, a mailbox
  // or an action — show that rather than an empty row.
  const hint = title || body
    ? null
    : (safeText(row.from) || safeText(row.mailbox_email) || safeText(row.mailbox)
       || safeText(row.user_name) || safeText(row.action) || safeText(row.entity_type));

  if (!title && !body && !hint) return null;   // nothing to render — skip it

  return {
    id,
    type: safeText(row.type) || safeText(row.kind) || 'system',
    title: title || hint || 'Notification',
    body: (title ? body : (body || null)) || null,
    created_at: safeText(row.created_at) || safeText(row.createdAt) || null,
    read: row.read === true,
    link: safeText(row.link) || null,
    entity_type: safeText(row.entity_type) || null,
    entity_id: safeText(row.entity_id) || null,
  };
}

/** Map a whole list, dropping the rows that have nothing to show. */
export function safeNotifications(rows) {
  if (!Array.isArray(rows)) return [];
  const out = [];
  for (let i = 0; i < rows.length; i += 1) {
    const row = safeNotification(rows[i], i);
    if (row) out.push(row);
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* preview text                                                        */
/* ------------------------------------------------------------------ */
/**
 * A live card read, literally:
 *   body { -webkit-text-size-adjust: 100%; ... } #backgroundTable ...
 * — the <style> block of a templated email being used as the preview, because
 * the snippet was the first N characters of the raw body.
 *
 * The real fix is server side (backend/routes/notify_center.py preview_text(),
 * which every /api/notify-center consumer gets for free, popup included).  This
 * is the SAME algorithm for the one consumer the server fix cannot reach: the
 * Header bell still reads the legacy `GET /api/notifications`, whose rows come
 * straight out of Mongo with the markup baked into `message`.
 *
 * Keep the two in step if either changes.
 */

const SCAN_CHARS = 60000;
export const PREVIEW_LIMIT = 140;
export const NO_PREVIEW = '(no preview)';

const DROP_BLOCKS = ['style', 'script', 'head', 'title', 'noscript', 'svg'];
const HIDDEN_STYLE = 'display\\s*:\\s*none|visibility\\s*:\\s*hidden|max-height\\s*:\\s*0'
  + '|max-width\\s*:\\s*0|font-size\\s*:\\s*0|line-height\\s*:\\s*0'
  + '|opacity\\s*:\\s*0|mso-hide\\s*:\\s*all';
const HIDDEN_RE = new RegExp(
  '<(div|span|td|p|table|tr)\\b[^>]*?(?:' + HIDDEN_STYLE + ')[^>]*>[\\s\\S]*?</\\1\\s*>', 'gi');
const PREHEADER_RE = new RegExp(
  '<(div|span|td|p)\\b[^>]*(?:class|id)\\s*=\\s*[\'"][^\'"]*pre(?:header|head)'
  + '[^\'"]*[\'"][^>]*>[\\s\\S]*?</\\1\\s*>', 'gi');
const COMMENT_RE = /<!--[\s\S]*?-->/g;
const TAG_RE = /<[^>]{0,4000}>/g;
const ZERO_WIDTH_RE = /[\u200b-\u200f\u2028\u2029\u202f\u2060\ufeff\u00ad]/g;
const RESIDUAL_TAG_RE = /<\s*\/?\s*[a-zA-Z!?]/;
const CSS_LEFTOVER_RE =
  /(?:^\s*[#.@*][\w-]|-webkit-|-moz-|-ms-|-o-|!important|\bmso-[\w-]+\s*:|\bdoctype\b|^\s*@(?:media|import|font-face|charset)\b)/i;
const HAS_WORD_RE = /[A-Za-z0-9\u00c0-\u024f\u0900-\u097f]/;

const ENTITIES = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  ndash: '\u2013', mdash: '\u2014', hellip: '\u2026', rsquo: '\u2019',
  lsquo: '\u2018', ldquo: '\u201c', rdquo: '\u201d', middot: '\u00b7',
  copy: '\u00a9', reg: '\u00ae', trade: '\u2122', eacute: '\u00e9',
};

/** Entity decode without touching the DOM (this must work in a worker/test). */
export function decodeEntities(text) {
  return String(text).replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (whole, body) => {
    if (body[0] === '#') {
      const code = body[1] === 'x' || body[1] === 'X'
        ? parseInt(body.slice(2), 16)
        : parseInt(body.slice(1), 10);
      if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return whole;
      try { return String.fromCodePoint(code); } catch (e) { return whole; }
    }
    const hit = ENTITIES[body.toLowerCase()];
    return hit === undefined ? whole : hit;
  });
}

const dropBlocks = (text) => DROP_BLOCKS.reduce((acc, tag) => acc
  .replace(new RegExp('<' + tag + '\\b[^>]*>[\\s\\S]*?</' + tag + '\\s*>', 'gi'), ' ')
  .replace(new RegExp('<' + tag + '\\b[^>]*>[\\s\\S]*$', 'i'), ' '), text);

const collapse = (text) => String(text || '')
  .replace(ZERO_WIDTH_RE, '')
  .replace(/\u00a0/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

/** True when what is left is still source, not prose. */
export function isMarkupResidue(text) {
  if (!text) return false;
  if (RESIDUAL_TAG_RE.test(text)) return true;
  if (text.indexOf('{') !== -1 || text.indexOf('}') !== -1) return true;
  if (CSS_LEFTOVER_RE.test(text)) return true;
  return !HAS_WORD_RE.test(text);
}

/**
 * An email body (HTML or plain) -> a short line a person can read.
 * Never returns markup; falls back to `fallback` (normally the subject), then
 * to "(no preview)" — or to `options.empty`, when the caller would rather show
 * nothing at all.
 */
export function previewText(value, options) {
  const opts = options || {};
  const limit = opts.limit || PREVIEW_LIMIT;
  let text = safeText(value);
  if (typeof text !== 'string') text = '';

  if (text && (text.indexOf('<') !== -1 || text.indexOf('&') !== -1 || text.indexOf('{') !== -1)) {
    text = text.slice(0, SCAN_CHARS)
      .replace(COMMENT_RE, ' ')
      .replace(/<!--[\s\S]*$/, ' ');
    text = dropBlocks(text)
      .replace(HIDDEN_RE, ' ')
      .replace(PREHEADER_RE, ' ')
      .replace(TAG_RE, ' ')
      .replace(/<[^>]*$/, ' ');

    if (text.indexOf('{') !== -1) {
      const closed = text.lastIndexOf('}');
      text = closed !== -1 ? text.slice(closed + 1) : text.slice(0, text.indexOf('{'));
    }
    text = decodeEntities(text).replace(TAG_RE, ' ');
  }

  text = collapse(text);
  if (isMarkupResidue(text)) text = '';

  if (!text) {
    const alt = collapse(safeText(opts.fallback) || '');
    if (alt && !isMarkupResidue(alt)) return alt.slice(0, limit);
    return opts.empty === undefined ? NO_PREVIEW : opts.empty;
  }

  if (text.length <= limit) return text;
  const cut = text.slice(0, limit);
  const space = cut.lastIndexOf(' ');
  return (space > limit * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,;:\-\u2013\u2014]+$/, '') + '\u2026';
}

export default {
  safeText, safeTextOr, safeNotification, safeNotifications,
  previewText, decodeEntities, isMarkupResidue, DISPLAY_KEYS,
};
